import React from 'react';
import { motion } from 'framer-motion';
import { FaInstagram, FaLinkedin } from 'react-icons/fa';
import { HiMail } from 'react-icons/hi';

interface FooterProps {
  onNavigate: (page: 'about' | 'blog' | 'terms' | 'privacy' | 'cookies') => void;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
};

export const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const socialLinks = [
    {
      href: 'https://www.instagram.com/pixelnet.now/',
      icon: FaInstagram,
      label: 'Follow us on Instagram',
      color: 'hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500',
    },
    {
      href: 'https://www.linkedin.com/in/saumya-dave-gamedev/',
      icon: FaLinkedin,
      label: 'Connect with us on LinkedIn',
      color: 'hover:bg-[#0077B5]',
    },
  ];

  const companyLinks = [
    { label: 'About Us', action: () => onNavigate('about') },
    { label: 'Blog', action: () => onNavigate('blog') },
  ];

  const legalLinks = [
    { label: 'Terms of Service', action: () => onNavigate('terms') },
    { label: 'Privacy Policy', action: () => onNavigate('privacy') },
    { label: 'Cookie Policy', action: () => onNavigate('cookies') },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-[var(--surface-2)] to-[var(--surface-1)] border-t border-[var(--surface-4)] mt-20 sm:mt-28 overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[var(--brand)] rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[var(--accent)] rounded-full blur-3xl" />
      </div>

      <motion.div
        className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 relative z-10"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Section */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <motion.div
                className="w-12 h-12 bg-gradient-to-br from-[var(--brand)] to-[var(--accent)] rounded-xl flex items-center justify-center shadow-lg"
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-7 w-7 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  {/* Magnifying glass circle */}
                  <circle cx="10" cy="10" r="7" strokeWidth={2} />
                  {/* Magnifying glass handle */}
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6 6" strokeWidth={2} />
                  {/* AI pixels/dots inside the magnifying glass */}
                  <rect x="7" y="7" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="9" y="7" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="11" y="7" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="6" y="9" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="8" y="9" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="10" y="9" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="12" y="9" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="7" y="11" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="9" y="11" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="11" y="11" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="8" y="13" width="1.5" height="1.5" fill="currentColor" />
                  <rect x="10" y="13" width="1.5" height="1.5" fill="currentColor" />
                </svg>
              </motion.div>
              <span className="text-2xl sm:text-3xl font-bold font-serif text-[var(--text-1)]">
                CompareLens
              </span>
            </div>
            <p className="text-[var(--text-2)] leading-relaxed max-w-md mb-6 text-sm sm:text-base">
              Your personal AI expert for instant, data-driven laptop recommendations. 
              Find the perfect laptop tailored to your needs with cutting-edge artificial intelligence.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group w-11 h-11 flex items-center justify-center rounded-xl bg-[var(--surface-3)] text-[var(--text-2)] hover:text-white transition-all duration-300 ${social.color}`}
                  aria-label={social.label}
                  whileHover={{ scale: 1.1, y: -4 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <social.icon size={20} />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Company Links */}
          <motion.div variants={itemVariants}>
            <h3 className="text-base sm:text-lg font-bold text-[var(--text-1)] mb-5 relative inline-block">
              Company
              <span className="absolute -bottom-2 left-0 w-8 h-1 bg-gradient-to-r from-[var(--brand)] to-[var(--accent)] rounded-full" />
            </h3>
            <ul className="space-y-3 mt-6">
              {companyLinks.map((link, index) => (
                <li key={index}>
                  <motion.button
                    onClick={link.action}
                    className="text-[var(--text-2)] hover:text-[var(--brand)] transition-all duration-200 inline-flex items-center group"
                    whileHover={{ x: 4 }}
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-[var(--brand)] mr-0 group-hover:mr-2 transition-all duration-200" />
                    {link.label}
                  </motion.button>
                </li>
              ))}
              <li>
                <motion.a
                  href="mailto:davesaumya24@gmail.com"
                  className="text-[var(--text-2)] hover:text-[var(--brand)] transition-all duration-200 inline-flex items-center group"
                  whileHover={{ x: 4 }}
                >
                  <span className="w-0 group-hover:w-2 h-0.5 bg-[var(--brand)] mr-0 group-hover:mr-2 transition-all duration-200" />
                  <span className="flex items-center gap-1.5">
                    <HiMail size={16} />
                    Contact
                  </span>
                </motion.a>
              </li>
            </ul>
          </motion.div>

          {/* Legal Links */}
          <motion.div variants={itemVariants}>
            <h3 className="text-base sm:text-lg font-bold text-[var(--text-1)] mb-5 relative inline-block">
              Legal
              <span className="absolute -bottom-2 left-0 w-8 h-1 bg-gradient-to-r from-[var(--brand)] to-[var(--accent)] rounded-full" />
            </h3>
            <ul className="space-y-3 mt-6">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <motion.button
                    onClick={link.action}
                    className="text-[var(--text-2)] hover:text-[var(--brand)] transition-all duration-200 inline-flex items-center group"
                    whileHover={{ x: 4 }}
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-[var(--brand)] mr-0 group-hover:mr-2 transition-all duration-200" />
                    {link.label}
                  </motion.button>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Bottom Section */}
        <motion.div
          variants={itemVariants}
          className="mt-12 pt-8 border-t border-[var(--surface-4)]"
        >
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-[var(--text-3)]">
            <p className="text-center sm:text-left">
              © {new Date().getFullYear()} CompareLens. All rights reserved.
            </p>
            <p className="flex items-center gap-2 text-center sm:text-right">
              Made with
              <motion.span
                className="text-red-500 inline-block"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 1, ease: "easeInOut" }}
              >
                ❤️
              </motion.span>
              by <span className="font-semibold text-[var(--brand)]">Saumya Dave</span>
            </p>
          </div>
        </motion.div>
      </motion.div>
    </footer>
  );
};
